import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Slide,
    Switch,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import { Container, Selector } from '../../../../components'

import {
    AreaConfig,
    AreaStatusTag,
    IdentifiableNamedObject,
    ModelMap,
    Schedule,
    Unit,
    UnitMinified,
} from '../../../../models'

import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import { EditAreaStatusTagRequest } from '../../../../store'
import { ExpandLess, ExpandMore, FileCopy } from '@material-ui/icons'
import * as XLSX from 'xlsx'
import { typographyStyle } from '../../../../theme/typographyStyle'
import { usePagination } from '../../../../hooks'

type UpdateAreaStatusTagMethod = (req: EditAreaStatusTagRequest) => void

const NO_FILTER = 0
const COMPLETE = 1
const PARTIAL = 2
const INCOMPLETE = 3

const ALL_SERVICE_TYPES = 0
const FULLY_VACANT = 1
const PARTIAL_SERVICE = 2

interface KeyTrackingUnit extends UnitMinified {
    area_status_tags: AreaStatusTag[]
    isPartialService: boolean
}

const getUnitLocation = (unit: KeyTrackingUnit) => {
    const areaStatusTag = unit.area_status_tags[0]

    if (areaStatusTag === undefined) {
        return ''
    }

    return areaStatusTag.folder.path + areaStatusTag.folder.name
}

const getSortedKeyTrackingUnits = (unitList: KeyTrackingUnit[]) => {
    return [...unitList].sort((a, b) => {
        const locationCompare = getUnitLocation(a).localeCompare(
            getUnitLocation(b),
        )

        if (locationCompare !== 0) {
            return locationCompare
        }

        return a.name.localeCompare(b.name)
    })
}

const getSortedAreaStatusTags = (areaStatusTags: AreaStatusTag[]) => {
    return [...areaStatusTags].sort((a, b) => {
        return a.area.area_label.localeCompare(b.area.area_label)
    })
}

const getKeyTrackingFileName = (schedule?: Schedule) => {
    const scheduleName = schedule?.name ?? 'key_tracking'

    return `${scheduleName.replace(/[^\w-]+/g, '_')}_key_tracking.xlsx`
}

const downloadKeyTrackingReport = (
    unitList: KeyTrackingUnit[],
    schedule?: Schedule,
) => {
    const sortedUnits = getSortedKeyTrackingUnits(unitList)

    const summaryRows = sortedUnits.map((unit) => {
        const keysIn = unit.area_status_tags.reduce((count, areaStatusTag) => {
            return areaStatusTag.key ? count + 1 : count
        }, 0)
        const totalKeys = unit.area_status_tags.length

        return [
            getUnitLocation(unit),
            unit.name,
            keysIn,
            totalKeys,
            totalKeys === 0 ? '' : `${keysIn}/${totalKeys}`,
            unit.isPartialService ? 'Partial' : 'Fully Vacant',
        ]
    })

    const detailRows = sortedUnits.flatMap((unit) => {
        return getSortedAreaStatusTags(
            unit.area_status_tags,
        ).map((areaStatusTag) => [
            getUnitLocation(unit),
            unit.name,
            areaStatusTag.area.area_config.name,
            areaStatusTag.area.area_label,
            areaStatusTag.area_status_config.name,
            areaStatusTag.key ? 'In' : 'Out',
            unit.isPartialService ? 'Partial' : 'Fully Vacant',
        ])
    })

    const summarySheet = XLSX.utils.aoa_to_sheet([
        [
            'Location',
            'Unit',
            'Keys In',
            'Total Keys',
            'Progress',
            'Partial/Fully Vacant',
        ],
        ...summaryRows,
    ])
    summarySheet['!cols'] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 10 },
        { wch: 10 },
        { wch: 12 },
        { wch: 15 },
    ]

    const detailSheet = XLSX.utils.aoa_to_sheet([
        [
            'Location',
            'Unit',
            'Area',
            'Area Label',
            'Bed Status',
            'Key Status',
            'Partial/Fully Vacant',
        ],
        ...detailRows,
    ])
    detailSheet['!cols'] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 20 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Details')

    const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
        compression: true,
    })

    const blob = new Blob([excelBuffer], {
        type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = getKeyTrackingFileName(schedule)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    window.URL.revokeObjectURL(url)
}

interface Props {
    open: boolean
    schedule?: Schedule
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagList?: AreaStatusTag[]
    updateAreaStatusTag: UpdateAreaStatusTagMethod
    handleClose: () => void
    visibleUnits: Unit[]
}

export const KeyTrackingModal = (props: Props) => {
    const {
        open,
        handleClose,
        areaStatusTagList,
        updateAreaStatusTag,
        visibleUnits,
    } = props

    const theme = useTheme()

    const [searchText, setSearchText] = useState('')
    const [unitFilter, setUnitFilter] = useState(NO_FILTER)
    const [serviceFilter, setServiceFilter] = useState(ALL_SERVICE_TYPES)
    const [unitList, setUnitList] = useState<KeyTrackingUnit[]>([])

    const visibleUnitOrderMap = useMemo(() => {
        const map = new Map<number, number>()
        visibleUnits.forEach((unit, idx) => map.set(unit.id, idx))
        return map
    }, [visibleUnits])

    useEffect(() => {
        const unitMap: ModelMap<KeyTrackingUnit> = {}

        areaStatusTagList?.forEach((areaStatusTag) => {
            if (!areaStatusTag.area.area_config.occupiable) {
                return
            }

            if (unitMap[areaStatusTag.unit.id] === undefined) {
                unitMap[areaStatusTag.unit.id] = {
                    ...areaStatusTag.unit,
                    area_status_tags: [],
                    isPartialService: false,
                }
            }

            const unit = unitMap[areaStatusTag.unit.id]

            if (unit === undefined) {
                return
            }

            if (areaStatusTag.area_status_config.should_service) {
                unit.area_status_tags.push(areaStatusTag)
            } else {
                unit.isPartialService = true
            }
        })
        const newUnitList: KeyTrackingUnit[] = []

        Object.keys(unitMap).map((key) => {
            if (key !== 'length') {
                const unit = unitMap[Number(key)] as KeyTrackingUnit

                if (unit.area_status_tags.length > 0) {
                    newUnitList.push(unit)
                }
            }
        })

        newUnitList.sort((a, b) => {
            const aIdx = visibleUnitOrderMap.get(a.id)
            const bIdx = visibleUnitOrderMap.get(b.id)

            if (aIdx !== undefined && bIdx !== undefined) {
                return aIdx - bIdx
            }
            if (aIdx !== undefined) return -1
            if (bIdx !== undefined) return 1

            const locationCompare = getUnitLocation(a).localeCompare(
                getUnitLocation(b),
            )
            if (locationCompare !== 0) return locationCompare
            return a.name.localeCompare(b.name)
        })

        setUnitList(newUnitList)
    }, [open, areaStatusTagList, visibleUnitOrderMap])

    let keysIn = 0
    let totalCount = 0
    areaStatusTagList?.forEach((ast) => {
        if (
            ast.area.area_config.occupiable &&
            ast.area_status_config.should_service
        ) {
            if (ast.key) {
                keysIn += 1
            }
            totalCount += 1
        }
    })

    const HeadCellStyle: React.CSSProperties = {
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        fontSize: '17px',
        flex: 2,
    }

    const filterUnits = (unit: KeyTrackingUnit) => {
        const unitName = unit.name.toLocaleLowerCase()
        const search = searchText.toLocaleLowerCase()

        const completeCount =
            unit.area_status_tags?.reduce((count, ast) => {
                if (ast.key) {
                    return count + 1
                }
                return count
            }, 0) ?? 0

        let completenessFilter = true

        switch (unitFilter) {
            case COMPLETE:
                completenessFilter =
                    completeCount === unit.area_status_tags?.length
                break
            case PARTIAL:
                completenessFilter =
                    completeCount > 0 &&
                    completeCount < (unit.area_status_tags?.length ?? 0)
                break
            case INCOMPLETE:
                completenessFilter = completeCount === 0
                break
        }

        let serviceTypeFilter = true

        switch (serviceFilter) {
            case FULLY_VACANT:
                serviceTypeFilter = !unit.isPartialService
                break
            case PARTIAL_SERVICE:
                serviceTypeFilter = unit.isPartialService
                break
        }

        return (
            unitName.includes(search) && completenessFilter && serviceTypeFilter
        )
    }

    const filteredUnits = useMemo(() => unitList.filter(filterUnits), [
        unitList,
        searchText,
        unitFilter,
        serviceFilter,
    ])

    const { pageData, numPages, page, setPage, start, end } = usePagination(
        filteredUnits,
        25,
    )

    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setPage(1)
    }, [searchText, unitFilter, serviceFilter])

    useEffect(() => {
        listRef.current?.scrollTo({ top: 0 })
    }, [page])

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        overflow: 'hidden',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        outline: 'none',
                        borderRadius: '20px',
                        width: '1200px',
                        height: 'calc(100vh - 80px)',
                        maxHeight: 'calc(100vh - 80px)',
                        maxWidth: 'calc(100vw - 80px)',
                        padding: theme.spacing(1, 4, 1, 4),
                    }}
                >
                    <Container
                        style={{
                            padding: theme.spacing(1, 0),
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            height: '100%',
                        }}
                        direction="column"
                    >
                        <Container alignItems="center">
                            <Container
                                style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    margin: theme.spacing(1, 0, 2, 0),
                                    flex: 1,
                                }}
                            >
                                Key Tracking
                            </Container>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={() => {
                                        handleClose()
                                    }}
                                >
                                    <CloseIcon
                                        fontSize="inherit"
                                        style={{
                                            color: theme.palette.darkGreen.main,
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Container>
                        <Container style={{ marginBottom: theme.spacing(2) }}>
                            <Container>
                                <TextField
                                    variant="outlined"
                                    placeholder="Search Unit"
                                    value={searchText}
                                    onChange={(e) =>
                                        setSearchText(e.target.value)
                                    }
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Container>
                            <Container flex={1} />
                            <Tooltip title="Get Spreadsheet">
                                <IconButton
                                    onClick={() => {
                                        downloadKeyTrackingReport(
                                            unitList,
                                            props.schedule,
                                        )
                                    }}
                                >
                                    <FileCopy fontSize="large" />
                                </IconButton>
                            </Tooltip>
                            <Selector
                                label="Completeness Filter"
                                data={[
                                    { id: NO_FILTER, name: 'No Filter' },
                                    { id: COMPLETE, name: 'Complete' },
                                    { id: PARTIAL, name: 'Partial' },
                                    { id: INCOMPLETE, name: 'Incomplete' },
                                ]}
                                currentValue={unitFilter}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        value: unknown
                                    }>,
                                ) => {
                                    setUnitFilter(event.target.value as number)
                                }}
                                getDisplayString={(
                                    idObject: IdentifiableNamedObject,
                                ) => idObject.name}
                                customStyle={{
                                    formControl: {
                                        width: 200,
                                    },
                                }}
                            />
                            <Selector
                                label="Unit Filter"
                                data={[
                                    {
                                        id: ALL_SERVICE_TYPES,
                                        name: 'All Units',
                                    },
                                    {
                                        id: FULLY_VACANT,
                                        name: 'Fully Vacant',
                                    },
                                    {
                                        id: PARTIAL_SERVICE,
                                        name: 'Partial',
                                    },
                                ]}
                                currentValue={serviceFilter}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        value: unknown
                                    }>,
                                ) => {
                                    setServiceFilter(
                                        event.target.value as number,
                                    )
                                }}
                                getDisplayString={(
                                    idObject: IdentifiableNamedObject,
                                ) => idObject.name}
                                customStyle={{
                                    formControl: {
                                        width: 180,
                                        marginLeft: theme.spacing(1),
                                    },
                                }}
                            />
                        </Container>
                        <Container>
                            <Container style={{ ...HeadCellStyle }}>
                                Location
                            </Container>
                            <Container style={{ ...HeadCellStyle }}>
                                Unit
                            </Container>
                            <Container style={{ ...HeadCellStyle }}>
                                Keys In {keysIn}/{totalCount}
                            </Container>
                            <Container style={{ ...HeadCellStyle }}></Container>
                        </Container>
                        <Container
                            ref={listRef}
                            direction="column"
                            scrollY
                            style={{
                                overflowY: 'auto',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            {pageData.map((unit) => {
                                return (
                                    <KeyTrackingRowNew
                                        key={`KEY_TRACKING_UNIT_${unit.id}`}
                                        unit={unit}
                                        updateAreaStatusTag={
                                            updateAreaStatusTag
                                        }
                                    />
                                )
                            })}
                        </Container>
                        <Container
                            style={{
                                minHeight: 56,
                                flexShrink: 0,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: theme.spacing(1, 0),
                                borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                    minWidth: 140,
                                }}
                            >
                                {filteredUnits.length > 0
                                    ? `${start}–${end} of ${filteredUnits.length}`
                                    : 'No units'}
                            </span>
                            <Pagination
                                count={Math.max(numPages, 1)}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                                size="medium"
                                shape="rounded"
                                showFirstButton
                                showLastButton
                            />
                            <span style={{ minWidth: 140 }} />
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}

interface KeyTrackingRowProps {
    unit: KeyTrackingUnit
    updateAreaStatusTag: UpdateAreaStatusTagMethod
}

const KeyTrackingRowNew = (props: KeyTrackingRowProps) => {
    const { unit, updateAreaStatusTag } = props

    const theme = useTheme()

    let unitComplete = true
    let folderString = ''
    let numberComplete = 0

    unit.area_status_tags?.forEach((areaStatusTag, idx) => {
        if (areaStatusTag.key) {
            numberComplete++
        } else {
            unitComplete = false
        }

        if (idx === 0) {
            folderString = areaStatusTag.folder.path + areaStatusTag.folder.name
        }
    })

    const [areasOpen, setAreasOpen] = useState(!unitComplete)

    const CellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle1),
        flex: 1,
        marginLeft: theme.spacing(1),
        fontWeight: 700,
        alignSelf: 'center',
    }

    return (
        <Container
            style={{ flexDirection: 'column', marginBottom: theme.spacing(2) }}
        >
            {/* Unit Box */}
            <Container
                style={{
                    flex: 1,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.grey[700]}`,
                    justifyContent: 'space-between',
                    backgroundColor: unitComplete
                        ? theme.palette.primary.light
                        : theme.palette.grey[300],
                }}
            >
                <span style={CellStyle}>{folderString}</span>
                <span style={CellStyle}>{unit.name}</span>

                <span style={CellStyle}>
                    {numberComplete} / {unit.area_status_tags?.length}
                    {unit.isPartialService && ' (Partial)'}
                </span>

                <Container style={{ ...CellStyle, justifyContent: 'flex-end' }}>
                    <IconButton
                        onClick={() => {
                            setAreasOpen(!areasOpen)
                        }}
                    >
                        {areasOpen ? (
                            <ExpandLess color="disabled" fontSize="large" />
                        ) : (
                            <ExpandMore color="disabled" fontSize="large" />
                        )}
                    </IconButton>
                </Container>
            </Container>
            {/* Area Status Tag Boxes */}
            {areasOpen &&
                getSortedAreaStatusTags(unit.area_status_tags).map(
                    (areaStatusTag) => {
                        return (
                            <Container
                                key={`AREA_STATUS_TAG_${areaStatusTag.id}_${unit.id}`}
                                style={{
                                    flex: 1,
                                    border: `1px solid ${theme.palette.grey[700]}`,
                                    margin: theme.spacing(0, 3, 0, 3),
                                    borderTop: 'none',
                                }}
                            >
                                <span
                                    style={{
                                        ...CellStyle,
                                        fontWeight:
                                            theme.typography.fontWeightMedium,
                                    }}
                                >
                                    {areaStatusTag.area.area_config.name}{' '}
                                    {areaStatusTag.area.area_label} (
                                    {areaStatusTag.area_status_config.name})
                                </span>

                                <Container
                                    style={{
                                        ...CellStyle,
                                        fontWeight:
                                            theme.typography.fontWeightMedium,
                                    }}
                                >
                                    <span>Out</span>
                                    <Switch
                                        color="primary"
                                        onChange={() => {
                                            updateAreaStatusTag({
                                                id: areaStatusTag.id,
                                                body: {
                                                    key: !areaStatusTag.key,
                                                },
                                            })
                                        }}
                                        checked={areaStatusTag.key}
                                    />
                                    <span>In</span>
                                </Container>
                            </Container>
                        )
                    },
                )}
        </Container>
    )
}
